import { OAuthProvider } from '#modules/user/features/auth/constants/providers';

/**
 * Игры, у которых статистика реально собрана и рисуется карточкой, и провайдер, привязка
 * которого её включает. Список намеренно совпадает со слагами, по которым карточки
 * включаются в `useLolStats` / `useCs2Stats` / `useDotaStats` / `useFortniteStats`: нудж
 * не должен обещать то, чего после привязки не появится.
 *
 * `game.providers` с бэка для этого НЕ годится: там перечислены лаунчеры/магазины, а не
 * источники статистики. По EPIC_GAMES сейчас числятся Fortnite, Rocket League и The
 * Division 2 — статистику мы показываем только у первой, а на двух других нудж обещал
 * «статистику Fortnite» и после привязки не показывал ничего.
 */
const STATS_PROVIDER_BY_SLUG: Record<string, OAuthProvider> = {
  'league-of-legends': OAuthProvider.RIOT_GAMES,
  'dota-2': OAuthProvider.STEAM,
  dota2: OAuthProvider.STEAM,
  cs2: OAuthProvider.STEAM,
  'counter-strike-2': OAuthProvider.STEAM,
  // Fortnite: карточка статистики уже собрана (FortniteStatsBlock), данные идут из community-API
  // fortnite-api.com — официального API по игрокам у Epic нет ни у кого. Запросы уходят к вендору,
  // а не к Epic, и в самой карточке подписано, что это career-агрегат, а не ранг.
  fortnite: OAuthProvider.EPIC_GAMES,
  // Marathon: своей статистики в Steam нет (0 статов в схеме), внешнего API тоже нет — в
  // спецификации Bungie игра присутствует только как признак наличия аккаунта. Показываем
  // наигранные часы из библиотеки Steam (`GamePlaytimeBlock`), поэтому обещание нуджа
  // выполняется: привязал Steam — увидел часы.
  marathon: OAuthProvider.STEAM,
  // Deadlock: своего входа у игры нет, личность берётся из Steam-привязки — поллер ходит в
  // открытый deadlock-api.com по SteamID. Карточка (`DeadlockStatsBlock`) давно на профиле,
  // а в этой карте игры не было: плитка Deadlock оставалась единственной без кнопки статистики.
  deadlock: OAuthProvider.STEAM,
  // Destiny 2: единственный источник статистики — Bungie.net (`useDestinyStats`: Power, класс,
  // K/D в Crucible, часы). В Steam у игры ноль статов, поэтому звать сюда Steam — обещать то,
  // чего после привязки не появится: карточку рисует только привязка Bungie.
  'destiny-2': OAuthProvider.BUNGIE,
  // Valorant/TFT (Riot) — на аппруве, данных пока нет.
  //
  // Apex Legends: своего OAuth у игры нет и не будет — EA наружу вход не отдаёт. Но личность
  // игрока берётся из Steam: вендор статистики принимает SteamID64 как адрес аккаунта, поэтому
  // привязка Steam карточку действительно включает. Ввод ника руками запрещён, других путей нет.
  //
  // ⚠️ Единственное обещание в этом реестре, которое сбывается не у всех: Apex кроссплатформенный,
  // и тому, кто играет через приложение EA или с консоли, привязка Steam ничего не покажет.
  // Заведено сознательно — без нуджа о единственном рабочем пути не узнаёт вообще никто.
  'apex-legends': OAuthProvider.STEAM,
};

/**
 * Три показателя, которыми игровой блок открывается, — ими же пустое превью обещает, что
 * встанет на их место после привязки. Порядок и состав списка сверены с самими блоками
 * (`LolRankBlock`, `DotaStatsBlock`, `Cs2StatsBlock`, `FortniteStatsBlock`,
 * `DestinyStatsBlock`, `GamePlaytimeBlock`): превью, обещающее не те цифры, которые потом
 * появятся, хуже, чем отсутствие превью — человек привязывает аккаунт ради конкретного числа.
 *
 * Ключи, а не готовые подписи: файл читают и unit-тесты, а перевод живёт в компоненте.
 */
export type StatsMetricKey =
  | 'rank'
  | 'winRate'
  | 'kda'
  | 'kd'
  | 'wins'
  | 'matches'
  | 'gpm'
  | 'adr'
  | 'hours'
  | 'power'
  | 'class'
  | 'level'
  | 'lastTwoWeeks';

const STATS_METRICS_BY_SLUG: Record<string, StatsMetricKey[]> = {
  'league-of-legends': ['rank', 'winRate', 'kda'],
  'dota-2': ['matches', 'winRate', 'gpm'],
  dota2: ['matches', 'winRate', 'gpm'],
  cs2: ['winRate', 'adr', 'hours'],
  'counter-strike-2': ['winRate', 'adr', 'hours'],
  fortnite: ['kd', 'wins', 'matches'],
  'destiny-2': ['power', 'class', 'kd'],
  // Deadlock: те же три числа, что показывает карточка, — ранг, матчи и доля побед.
  deadlock: ['rank', 'matches', 'winRate'],
  // У Marathon статистики нет ни в Steam, ни снаружи — привязка даёт только наигранные часы
  // из библиотеки. Обещаем ровно их: два показателя вместо трёх честнее, чем выдуманный K/D.
  marathon: ['hours', 'lastTwoWeeks'],
  // Apex: обещаем ровно те два числа, которые есть у КАЖДОГО игрока. Убийства, урон и победы
  // сюда не годятся — они приходят, только если человек надел соответствующий трекер на баннер,
  // а у 21 из 59 проверенных живых аккаунтов не надет ни один. Ранг и уровень есть всегда.
  'apex-legends': ['rank', 'level'],
};

// Свободная форма, совместимая с типом Game из gql.tada.
type GameLike = {
  slug?: string | null;
};

/** Провайдер статистики для игры или `null`, если статистики по этой игре у нас нет. */
export const statsProviderForGame = (game: GameLike | null | undefined): OAuthProvider | null => {
  if (!game?.slug) {
    return null;
  }

  return STATS_PROVIDER_BY_SLUG[game.slug] ?? null;
};

/** Показатели, которые появятся у игры после привязки. Пустой список — статистики нет. */
export const statsMetricsForGame = (game: GameLike | null | undefined): StatsMetricKey[] => {
  if (!game?.slug) {
    return [];
  }

  return STATS_METRICS_BY_SLUG[game.slug] ?? [];
};

/**
 * Игры, где обещание нуджа сбывается ТОЛЬКО на PC.
 *
 * Apex кроссплатформенный, а личность игрока мы берём из привязки Steam: вендору статистики
 * уходит SteamID64 как адрес аккаунта. Тому, кто играет с консоли, привязка Steam не покажет
 * ничего — на проде это видно прямо: из 334 собранных игровых аккаунтов Apex все 334 имеют
 * platform=PC, консольных нет ни одного.
 *
 * До сих пор нудж звали всем подряд, сознательно: «без него о единственном рабочем пути не
 * узнает вообще никто». Но за 90 дней 172 заявки по Apex созданы с консоли (137 PlayStation,
 * 35 Xbox), и для их авторов это обещание, которое не сбудется. Показываем только там, где
 * платформа заявки говорит про PC.
 *
 * Список именно про «наш путь опознания не работает на консоли», а не про «игра только для PC»:
 * у Destiny 2 (Bungie) и Fortnite (аккаунт Epic) личность кроссплатформенная, и там обещание
 * держится на любой платформе.
 */
const PC_ONLY_STATS_SLUGS = new Set(['apex-legends']);

/**
 * Сбудется ли обещание статистики на этих платформах.
 *
 * `platformNames` — платформы заявки (у заявки она всегда одна, но поле массив). Пустой список
 * или `undefined` означает «платформа неизвестна»: тогда не скрываем, иначе нудж пропал бы в
 * профиле, где заявки на экране нет вовсе.
 */
export const statsPromiseHolds = (
  game: GameLike | null | undefined,
  platformNames: string[] | undefined
): boolean => {
  if (!game?.slug || !PC_ONLY_STATS_SLUGS.has(game.slug)) {
    return true;
  }

  if (!platformNames || platformNames.length === 0) {
    return true;
  }

  return platformNames.some((name) => name?.toUpperCase() === 'PC');
};
